<template>
	<div class="date-range-picker" :class="{ 'no-sidebar': props.hideSidebar }" ref="pickerRef">
		<button type="button" class="date-range-input" @click="togglePicker">
			<div class="date-text">{{ formattedDateRange }}</div>
			<VIcon
				v-if="(props.modelValue?.startDate || appliedRange.startDate) && (!props.hideClearIcon || !isCleared)"
				class="clear-icon"
				@click.stop="handleClear"
			>
				mdi-close
			</VIcon>
			<VIcon v-else class="calendar-icon">mdi-calendar-month</VIcon>
		</button>

		<!-- Fullscreen overlay mask to block outside background clicks -->
		<Teleport to="body">
			<div v-if="isOpen" class="picker-modal-backdrop" @click.stop></div>
		</Teleport>

		<Transition name="dropdown">
			<div v-if="isOpen" class="dropdown-panel" :class="{ 'is-disposition': selectedView === 'daily_disposition' }">
				<div class="panel-layout">
					<div class="layout-main">
						<div v-if="!props.hideSidebar" class="sidebar">
							<div class="sidebar-header">
								<div class="sidebar-title">{{ $t('date_picker_placeholder_view') }}</div>
							</div>

							<div class="sidebar-options">
								<label
									v-for="option in viewOptions"
									:key="option.value"
									class="radio-option"
									:class="{ active: selectedView === option.value }"
								>
									<div class="selection-control">
										<VIcon class="radio-icon">
											{{ selectedView === option.value ? 'mdi-radiobox-marked' : 'mdi-radiobox-blank' }}
										</VIcon>
									</div>
									<input
										type="radio"
										name="view"
										:value="option.value"
										v-model="selectedView"
										@change="handleViewChange"
										class="hidden-radio"
									/>
									<span class="option-label">{{ option.label }}</span>
								</label>
							</div>
						</div>

						<div v-if="!props.hideSidebar" class="divider-line"></div>

						<div class="v-picker__body">
							<div v-if="selectedView === 'daily_disposition'" class="disposition-view">
								<div class="disposition-column">
									<div class="disposition-header">
										<div class="disposition-title">{{ $t('date_picker_range_past_days') }}</div>
									</div>

									<label v-for="days in dayOptions" :key="'past-' + days" class="radio-option-inline">
										<input type="radio" name="pastDays" :value="days" v-model.number="pastDays" @change="pastDaysCustom = ''" />
										<span>{{ days }}</span>
									</label>

									<label class="radio-option-inline">
										<input type="radio" name="pastDays" value="custom" :checked="!!pastDaysCustom" />
										<input
											type="number"
											v-model="pastDaysCustom"
											:placeholder="$t('date_picker_placeholder_custom')"
											class="custom-input"
											@focus="pastDays = null"
										/>
									</label>
								</div>
							</div>

							<div v-else class="calendar-wrapper">
								<CalendarMonth
									:year="startYear"
									:month="startMonth"
									:start-date="tempStartDate"
									:end-date="tempEndDate"
									:is-preset="isCalendarPreset"
									:show-left-nav="true"
									:show-right-nav="true"
									:disable-dropdowns="false"
									@update:year="startYear = $event"
									@update:month="startMonth = $event"
									@date-click="handleDateClick"
									@navigate="handleStartMonthNavigate"
								/>
							</div>
						</div>

						<div class="divider-line"></div>

						<div class="v-picker__body">
							<div v-if="selectedView === 'daily_disposition'" class="disposition-view">
								<div class="disposition-column">
									<div class="disposition-header">
										<div class="disposition-title">{{ $t('date_picker_range_future_days') }}</div>
									</div>

									<label v-for="days in dayOptions" :key="'future-' + days" class="radio-option-inline">
										<input type="radio" name="futureDays" :value="days" v-model.number="futureDays" @change="futureDaysCustom = ''" />
										<span>{{ days }}</span>
									</label>

									<label class="radio-option-inline">
										<input type="radio" name="futureDays" value="custom" :checked="!!futureDaysCustom" />
										<input
											type="number"
											v-model="futureDaysCustom"
											:placeholder="$t('date_picker_placeholder_custom')"
											class="custom-input"
											@focus="futureDays = null"
										/>
									</label>
								</div>
							</div>

							<CalendarMonth
								v-else
								:year="endYear"
								:month="endMonth"
								:start-date="tempStartDate"
								:end-date="tempEndDate"
								:is-preset="isCalendarPreset"
								:show-left-nav="true"
								:show-right-nav="true"
								:disable-dropdowns="false"
								@update:year="endYear = $event"
								@update:month="endMonth = $event"
								@date-click="handleDateClick"
								@navigate="handleEndMonthNavigate"
							/>
						</div>
					</div>

					<div v-if="rangeError" class="range-error-message">
						<VIcon class="error-icon">mdi-alert-circle</VIcon>
						<span>{{ rangeError }}</span>
					</div>

					<div class="actions">
						<button type="button" class="btn-text-only mr-4" @click="handleCancel">
							{{ $t('action_cancel') }}
						</button>
						<button v-if="selectedView === 'daily_disposition'" type="button" class="btn-apply" @click="handleApplyDisposition">
							<div class="btn-content">
								<span class="btn-text">{{ $t('action_apply') }}</span>
							</div>
						</button>
						<button v-else type="button" class="btn-apply" :disabled="selectedView === 'custom' && !canApply" @click="handleApply">
							<div class="btn-content">
								<span class="btn-text">{{ $t('action_apply') }}</span>
							</div>
						</button>
					</div>
				</div>
			</div>
		</Transition>
	</div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';
import type { DateView, DateRange } from '../types/date-range-picker';
import CalendarMonth from './CalendarMonth.vue';
import i18n from '@/plugins/i18n';
import { useDateTime } from '../../../../components/composables/useDateTime';
const { formatDate } = useDateTime();

const STORAGE_VIEW_KEY = 'date_picker_selected_view';
const STORAGE_DISPOSITION_KEY = 'date_picker_disposition_config';

const DAY_OPTIONS = [2, 3, 5, 7] as const;
const DEFAULT_PAST_DAYS = 2;
const DEFAULT_FUTURE_DAYS = 2;

interface Props {
	modelValue?: DateRange;
	publishedRange?: DateRange;
	planningRange?: DateRange;
	hideSidebar?: boolean;
	hideClearIcon?: boolean;
	view?: DateView;
	alwaysShowDateRange?: boolean;
	allowPresetEditing?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	modelValue: () => ({ startDate: null, endDate: null }),
	publishedRange: () => ({ startDate: null, endDate: null }),
	planningRange: () => ({ startDate: null, endDate: null }),
	hideSidebar: false,
	hideClearIcon: false,
	view: undefined,
	alwaysShowDateRange: false,
	allowPresetEditing: false,
});

const emit = defineEmits<{
	'update:modelValue': [value: DateRange];
	change: [value: DateRange];
	'update:view': [value: { value: DateView; label: string }];
}>();

const isOpen = ref(false);
const isCleared = ref(false);

const selectedView = ref<DateView>('custom');
const appliedView = ref<DateView>('custom');
const appliedRange = ref<DateRange>({ startDate: null, endDate: null });

const lastEmitted = ref<{
	startKey: string | null;
	endKey: string | null;
	view: DateView | null;
}>({
	startKey: null,
	endKey: null,
	view: null,
});

const lastView = ref<DateView>('custom');
const pickerRef = ref<HTMLElement | null>(null);
const rangeError = ref<string | null>(null);

const startYear = ref(new Date().getFullYear());
const startMonth = ref(new Date().getMonth());
const endYear = ref(new Date().getFullYear());
const endMonth = ref(new Date().getMonth() + 1);

const tempStartDate = ref<Date | null>(null);
const tempEndDate = ref<Date | null>(null);

// Counts successful boundary adjustments on a preset-derived range. Allows up to two
// (one for the start, one for the end); after that the normal custom two-click flow
// resumes. Reset when the popover reopens or the view is changed manually.
const presetEditClicks = ref(0);

const dayOptions = DAY_OPTIONS;
const pastDays = ref<number | null>(DEFAULT_PAST_DAYS);
const futureDays = ref<number | null>(DEFAULT_FUTURE_DAYS);
const pastDaysCustom = ref('');
const futureDaysCustom = ref('');

const viewOptions = computed(() => [
	{ value: 'published' as DateView, label: i18n.t('presets.published') },
	{ value: 'planning' as DateView, label: i18n.t('presets.planning') },
	{ value: 'daily_disposition' as DateView, label: i18n.t('presets.dailyDisposition') },
	{ value: 'custom' as DateView, label: i18n.t('presets.custom') },
]);

const canApply = computed(() => tempStartDate.value !== null && tempEndDate.value !== null);

// When preset editing is enabled, the planning/published calendars stay fully
// interactive (day clicks + navigation) so the user can adjust the range directly.
// Otherwise the original behaviour applies: only the custom view is interactive.
const isCalendarPreset = computed(() => (props.allowPresetEditing ? false : selectedView.value !== 'custom'));

const displayRange = computed(() => ({
	startDate: isOpen.value ? tempStartDate.value : appliedRange.value.startDate ?? props.modelValue?.startDate,
	endDate: isOpen.value ? tempEndDate.value : appliedRange.value.endDate ?? props.modelValue?.endDate,
}));

const formattedDateRange = computed(() => {
	if (isCleared.value) {
		return i18n.t('date_picker_range_placeholder');
	}

	const view = isOpen.value ? selectedView.value : appliedView.value;

	if (view !== 'custom' && !props.alwaysShowDateRange) {
		return viewOptions.value.find((opt) => opt.value === view)?.label ?? '';
	}

	const { startDate, endDate } = displayRange.value;

	if (!startDate || !endDate) {
		return i18n.t('date_picker_range_placeholder');
	}

	try {
		const startObj = new Date(startDate);
		const endObj = new Date(endDate);

		if (isNaN(startObj.getTime()) || isNaN(endObj.getTime())) {
			return i18n.t('date_picker_range_placeholder');
		}

		const start = formatDate(startObj.toISOString());
		const end = formatDate(endObj.toISOString());

		return `${start} ~ ${end}`;
	} catch (error) {
		return i18n.t('date_picker_range_placeholder');
	}
});

const saveViewToStorage = (view: DateView) => {
	try {
		if (view !== 'custom') {
			localStorage.setItem(STORAGE_VIEW_KEY, view);
		} else {
			localStorage.removeItem(STORAGE_VIEW_KEY);
			localStorage.removeItem(STORAGE_DISPOSITION_KEY);
		}
	} catch (e) {
		console.warn('Failed to save view state to localStorage:', e);
	}
};

const saveDispositionConfigToStorage = () => {
	try {
		const config = {
			pastDays: pastDays.value,
			futureDays: futureDays.value,
			pastDaysCustom: pastDaysCustom.value,
			futureDaysCustom: futureDaysCustom.value,
		};
		localStorage.setItem(STORAGE_DISPOSITION_KEY, JSON.stringify(config));
	} catch (e) {
		console.warn('Failed to save disposition config to localStorage:', e);
	}
};

const loadStoredView = (): DateView | null => {
	try {
		const view = localStorage.getItem(STORAGE_VIEW_KEY);
		return (view as DateView) || null;
	} catch (e) {
		return null;
	}
};

const loadStoredDispositionConfig = () => {
	try {
		const str = localStorage.getItem(STORAGE_DISPOSITION_KEY);
		return str ? JSON.parse(str) : null;
	} catch (e) {
		return null;
	}
};

const toDateKey = (date: any): string | null => {
	if (!date) return null;
	const d = date instanceof Date ? date : new Date(date);
	if (isNaN(d.getTime())) return null;
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const areDatesEqual = (d1: any, d2: any): boolean => {
	const k1 = toDateKey(d1);
	const k2 = toDateKey(d2);
	return k1 !== null && k2 !== null && k1 === k2;
};

const isDailyDispositionMatch = (start: any, end: any): boolean => {
	if (!start || !end) return false;

	const today = new Date();
	today.setHours(0, 0, 0, 0);

	const past = pastDaysCustom.value ? parseInt(pastDaysCustom.value) : pastDays.value || DEFAULT_PAST_DAYS;
	const future = futureDaysCustom.value ? parseInt(futureDaysCustom.value) : futureDays.value || DEFAULT_FUTURE_DAYS;

	const expectedStart = new Date(today);
	expectedStart.setDate(today.getDate() - past);

	const expectedEnd = new Date(today);
	expectedEnd.setDate(today.getDate() + future);

	return areDatesEqual(start, expectedStart) && areDatesEqual(end, expectedEnd);
};

const emitRangeUpdate = (range: DateRange, viewKey: DateView) => {
	const startKey = toDateKey(range.startDate);
	const endKey = toDateKey(range.endDate);

	const isAlreadyEmitted = lastEmitted.value.startKey === startKey && lastEmitted.value.endKey === endKey && lastEmitted.value.view === viewKey;

	const isAlreadyInProps =
		areDatesEqual(props.modelValue?.startDate, range.startDate) && areDatesEqual(props.modelValue?.endDate, range.endDate) && appliedView.value === viewKey;

	if (isAlreadyEmitted || isAlreadyInProps) {
		return;
	}

	lastEmitted.value = {
		startKey,
		endKey,
		view: viewKey,
	};

	emit('update:modelValue', range);

	const selectedOption = viewOptions.value.find((opt) => opt.value === viewKey);

	emit('update:view', {
		value: viewKey,
		label: selectedOption?.label || viewKey,
	});
};

const isRangeOverFourMonths = (start: Date | null, end: Date | null): boolean => {
	if (!start || !end) return false;

	const startObj = new Date(start);
	const endObj = new Date(end);

	const maxAllowedEnd = new Date(startObj);
	maxAllowedEnd.setMonth(maxAllowedEnd.getMonth() + 4);

	return endObj > maxAllowedEnd;
};

const getCappedRange = (start: Date | null, end: Date | null): { startDate: Date | null; endDate: Date | null } => {
	if (!start || !end) return { startDate: start, endDate: end };

	const startObj = new Date(start);
	const endObj = new Date(end);

	if (!isRangeOverFourMonths(startObj, endObj)) {
		return { startDate: startObj, endDate: endObj };
	}

	const today = new Date();
	today.setHours(0, 0, 0, 0);

	if (today <= startObj) {
		const cappedEnd = new Date(startObj);
		cappedEnd.setMonth(cappedEnd.getMonth() + 4);
		return { startDate: startObj, endDate: cappedEnd };
	}

	if (today >= endObj) {
		const cappedStart = new Date(endObj);
		cappedStart.setMonth(cappedStart.getMonth() - 4);
		return { startDate: cappedStart, endDate: endObj };
	}

	const idealStart = new Date(today);
	idealStart.setMonth(idealStart.getMonth() - 2);

	const idealEnd = new Date(today);
	idealEnd.setMonth(idealEnd.getMonth() + 2);

	if (idealStart < startObj) {
		const cappedEnd = new Date(startObj);
		cappedEnd.setMonth(cappedEnd.getMonth() + 4);
		return { startDate: startObj, endDate: cappedEnd > endObj ? endObj : cappedEnd };
	}

	if (idealEnd > endObj) {
		const cappedStart = new Date(endObj);
		cappedStart.setMonth(cappedStart.getMonth() - 4);
		return { startDate: cappedStart < startObj ? startObj : cappedStart, endDate: endObj };
	}

	return { startDate: idealStart, endDate: idealEnd };
};

const applyStoredPresetIfAvailable = (): boolean => {
	const storedView = loadStoredView();

	if (!storedView || storedView === 'custom') {
		return false;
	}

	if (storedView === 'published' && props.publishedRange?.startDate && props.publishedRange?.endDate) {
		const capped = getCappedRange(props.publishedRange.startDate, props.publishedRange.endDate);
		if (capped.startDate && capped.endDate) {
			appliedView.value = 'published';
			selectedView.value = 'published';
			appliedRange.value = capped;
			emitRangeUpdate(capped, 'published');
			return true;
		}
	} else if (storedView === 'planning' && props.planningRange?.startDate && props.planningRange?.endDate) {
		const capped = getCappedRange(props.planningRange.startDate, props.planningRange.endDate);
		if (capped.startDate && capped.endDate) {
			appliedView.value = 'planning';
			selectedView.value = 'planning';
			appliedRange.value = capped;
			emitRangeUpdate(capped, 'planning');
			return true;
		}
	} else if (storedView === 'daily_disposition') {
		const dispConfig = loadStoredDispositionConfig();
		if (dispConfig) {
			pastDays.value = dispConfig.pastDays;
			futureDays.value = dispConfig.futureDays;
			pastDaysCustom.value = dispConfig.pastDaysCustom;
			futureDaysCustom.value = dispConfig.futureDaysCustom;
		}

		const today = new Date();
		today.setHours(0, 0, 0, 0);

		const past = pastDaysCustom.value ? parseInt(pastDaysCustom.value) : pastDays.value || DEFAULT_PAST_DAYS;
		const future = futureDaysCustom.value ? parseInt(futureDaysCustom.value) : futureDays.value || DEFAULT_FUTURE_DAYS;

		const startDate = new Date(today);
		startDate.setDate(today.getDate() - past);

		const endDate = new Date(today);
		endDate.setDate(today.getDate() + future);

		const dispRange = { startDate, endDate };
		appliedView.value = 'daily_disposition';
		selectedView.value = 'daily_disposition';
		appliedRange.value = dispRange;
		emitRangeUpdate(dispRange, 'daily_disposition');
		return true;
	}

	return false;
};

const syncViewStateFromProps = (fromWatch = false) => {
	const storedView = loadStoredView();
	if (storedView && storedView !== 'custom') {
		return;
	}

	let start;
	let end;
	if (fromWatch) {
		start = props.modelValue?.startDate;
		end = props.modelValue?.endDate;
	} else {
		start = appliedRange.value.startDate ?? props.modelValue?.startDate;
		end = appliedRange.value.endDate ?? props.modelValue?.endDate;
	}

	const currentStartKey = toDateKey(start);
	const currentEndKey = toDateKey(end);

	if (currentStartKey === lastEmitted.value.startKey && currentEndKey === lastEmitted.value.endKey && appliedView.value !== 'custom') {
		return;
	}

	if (props.view) {
		selectedView.value = props.view;
		appliedView.value = props.view;
		return;
	}

	if (!start || !end) {
		return;
	}

	appliedRange.value = { startDate: start, endDate: end };

	if (
		props.publishedRange?.startDate &&
		props.publishedRange?.endDate &&
		areDatesEqual(start, props.publishedRange.startDate) &&
		areDatesEqual(end, props.publishedRange.endDate)
	) {
		selectedView.value = 'published';
		appliedView.value = 'published';
	} else if (
		props.planningRange?.startDate &&
		props.planningRange?.endDate &&
		areDatesEqual(start, props.planningRange.startDate) &&
		areDatesEqual(end, props.planningRange.endDate)
	) {
		selectedView.value = 'planning';
		appliedView.value = 'planning';
	} else if (isDailyDispositionMatch(start, end)) {
		selectedView.value = 'daily_disposition';
		appliedView.value = 'daily_disposition';
	} else {
		selectedView.value = 'custom';
		appliedView.value = 'custom';
	}
};

watch(
	[() => props.modelValue, () => props.publishedRange, () => props.planningRange, () => props.view],
	([newModelValue], [oldModelValue]) => {
		if (isOpen.value) return;

		const newStartKey = toDateKey(newModelValue?.startDate);
		const newEndKey = toDateKey(newModelValue?.endDate);

		const oldStartKey = toDateKey(oldModelValue?.startDate);
		const oldEndKey = toDateKey(oldModelValue?.endDate);

		const modelValueChanged = newStartKey !== oldStartKey || newEndKey !== oldEndKey;

		if (modelValueChanged) {
			lastEmitted.value = {
				startKey: null,
				endKey: null,
				view: null,
			};

			isCleared.value = newStartKey === null && newEndKey === null;

			appliedRange.value = {
				startDate: newModelValue?.startDate ?? null,
				endDate: newModelValue?.endDate ?? null,
			};

			syncViewStateFromProps(true);

			if (isCleared.value) {
				selectedView.value = 'custom';
				appliedView.value = 'custom';
				tempStartDate.value = null;
				tempEndDate.value = null;
			}

			return;
		}

		isCleared.value = false;

		const appliedFromStorage = applyStoredPresetIfAvailable();

		if (!appliedFromStorage) {
			syncViewStateFromProps(true);
		}
	},
	{ deep: true, immediate: true }
);

watch([startMonth, startYear], () => {
	if (selectedView.value === 'custom') {
		updateEndCalendarFromStart();
	}
});

onMounted(() => {
	document.addEventListener('click', handleClickOutside);
});

onBeforeUnmount(() => {
	document.removeEventListener('click', handleClickOutside);
});

const updateEndCalendarFromStart = () => {
	endMonth.value = startMonth.value + 1;
	endYear.value = startYear.value;

	if (endMonth.value > 11) {
		endMonth.value = 0;
		endYear.value++;
	}
};

const syncCalendars = () => {
	if (tempStartDate.value) {
		startYear.value = tempStartDate.value.getFullYear();
		startMonth.value = tempStartDate.value.getMonth();
	}
	if (tempEndDate.value) {
		endYear.value = tempEndDate.value.getFullYear();
		endMonth.value = tempEndDate.value.getMonth();
	}
	if (startYear.value === endYear.value && startMonth.value === endMonth.value) {
		endMonth.value = (endMonth.value + 1) % 12;
		if (endMonth.value === 0) {
			endYear.value++;
		}
	}
};

const initializeDates = () => {
	rangeError.value = null;
	presetEditClicks.value = 0;

	if (props.hideSidebar) {
		selectedView.value = 'custom';
		tempStartDate.value = props.modelValue.startDate;
		tempEndDate.value = props.modelValue.endDate;
	} else {
		selectedView.value = appliedView.value;

		if (selectedView.value === 'published' && props.publishedRange.startDate && props.publishedRange.endDate) {
			const capped = getCappedRange(props.publishedRange.startDate, props.publishedRange.endDate);
			tempStartDate.value = capped.startDate;
			tempEndDate.value = capped.endDate;
		} else if (selectedView.value === 'planning' && props.planningRange.startDate && props.planningRange.endDate) {
			const capped = getCappedRange(props.planningRange.startDate, props.planningRange.endDate);
			tempStartDate.value = capped.startDate;
			tempEndDate.value = capped.endDate;
		} else {
			tempStartDate.value = appliedRange.value.startDate || props.modelValue.startDate;
			tempEndDate.value = appliedRange.value.endDate || props.modelValue.endDate;
		}
	}

	const today = new Date();
	const pivotDate = tempStartDate.value || today;
	startYear.value = pivotDate.getFullYear();
	startMonth.value = pivotDate.getMonth();

	updateEndCalendarFromStart();
};

const handleViewChange = () => {
	rangeError.value = null;
	presetEditClicks.value = 0;

	if (selectedView.value === 'custom') {
		if (lastView.value !== 'custom') {
			tempStartDate.value = props.modelValue.startDate;
			tempEndDate.value = props.modelValue.endDate;
		}
	} else if (selectedView.value === 'published' && props.publishedRange.startDate && props.publishedRange.endDate) {
		const capped = getCappedRange(props.publishedRange.startDate, props.publishedRange.endDate);
		tempStartDate.value = capped.startDate;
		tempEndDate.value = capped.endDate;
		syncCalendars();
	} else if (selectedView.value === 'planning' && props.planningRange.startDate && props.planningRange.endDate) {
		const capped = getCappedRange(props.planningRange.startDate, props.planningRange.endDate);
		tempStartDate.value = capped.startDate;
		tempEndDate.value = capped.endDate;
		syncCalendars();
	}

	const selectedOption = viewOptions.value.find((opt) => opt.value === selectedView.value);
	emit('update:view', { value: selectedView.value, label: selectedOption?.label || selectedView.value });

	lastView.value = selectedView.value;
};

// Switch the active view to custom without routing through handleViewChange, whose
// custom branch would reset the temp dates from modelValue and discard the edit.
const switchToCustomView = () => {
	selectedView.value = 'custom';
	lastView.value = 'custom';
	const customOption = viewOptions.value.find((opt) => opt.value === 'custom');
	emit('update:view', { value: 'custom', label: customOption?.label || 'custom' });
};

// Preset editing: a click on the calendar while planning/published is selected moves
// the boundary closest to the clicked date (ties move the start), then switches the
// selection to custom. The other boundary stays put, so the range shrinks or expands.
const adjustPresetBoundary = (clicked: Date) => {
	rangeError.value = null;

	const start = tempStartDate.value ? new Date(tempStartDate.value) : null;
	const end = tempEndDate.value ? new Date(tempEndDate.value) : null;

	// No complete range to adjust — fall back to starting a fresh custom selection
	// and hand control back to the normal two-click flow to complete the range.
	if (!start || !end) {
		tempStartDate.value = new Date(clicked);
		tempEndDate.value = null;
		presetEditClicks.value = 0;
		switchToCustomView();
		return;
	}

	const distToStart = Math.abs(clicked.getTime() - start.getTime());
	const distToEnd = Math.abs(clicked.getTime() - end.getTime());

	let newStart = start;
	let newEnd = end;
	// Ties (distToStart === distToEnd) move the start, per requirement.
	if (distToStart <= distToEnd) {
		newStart = new Date(clicked);
	} else {
		newEnd = new Date(clicked);
	}

	// Keep the boundaries chronological.
	if (newStart > newEnd) {
		[newStart, newEnd] = [newEnd, newStart];
	}

	// Respect the existing 4-month cap — if exceeded, leave the range unchanged.
	if (isRangeOverFourMonths(newStart, newEnd)) {
		rangeError.value = i18n.t('date_picker_error_exceeds_limit', 'Date range cannot exceed 4 months.');
		return;
	}

	tempStartDate.value = newStart;
	tempEndDate.value = newEnd;
	// Count this adjustment; the next click can move the other boundary, after which
	// the normal custom two-click flow takes over.
	presetEditClicks.value += 1;
	switchToCustomView();
};

const handleDateClick = (date: Date) => {
	// First click enters via the preset view; the second is allowed via the counter so
	// both start and end can be adjusted. A third click falls through to custom below.
	if (props.allowPresetEditing && (selectedView.value === 'planning' || selectedView.value === 'published' || presetEditClicks.value === 1)) {
		adjustPresetBoundary(new Date(date));
		return;
	}

	if (selectedView.value !== 'custom') return;

	rangeError.value = null;

	if (!tempStartDate.value || (tempStartDate.value && tempEndDate.value)) {
		tempStartDate.value = new Date(date);
		tempEndDate.value = null;
	} else {
		const clickedDate = new Date(date);
		let prospectiveStart = new Date(tempStartDate.value);
		let prospectiveEnd = new Date(clickedDate);

		if (clickedDate < tempStartDate.value) {
			prospectiveEnd = new Date(tempStartDate.value);
			prospectiveStart = clickedDate;
		}

		if (isRangeOverFourMonths(prospectiveStart, prospectiveEnd)) {
			rangeError.value = i18n.t('date_picker_error_exceeds_limit', 'Date range cannot exceed 4 months.');
			tempStartDate.value = clickedDate;
			tempEndDate.value = null;
		} else {
			tempStartDate.value = prospectiveStart;
			tempEndDate.value = prospectiveEnd;
		}
	}
};

const handleStartMonthNavigate = (direction: number) => {
	const date = new Date(startYear.value, startMonth.value + direction, 1);
	startMonth.value = date.getMonth();
	startYear.value = date.getFullYear();

	updateEndCalendarFromStart();
};

const handleEndMonthNavigate = (direction: number) => {
	const endDate = new Date(endYear.value, endMonth.value + direction, 1);
	endYear.value = endDate.getFullYear();
	endMonth.value = endDate.getMonth();

	const startDate = new Date(endYear.value, endMonth.value - 1, 1);
	startYear.value = startDate.getFullYear();
	startMonth.value = startDate.getMonth();
};

const handleApply = async () => {
	if (selectedView.value === 'custom' && !canApply.value) return;

	appliedView.value = selectedView.value;
	appliedRange.value = {
		startDate: tempStartDate.value,
		endDate: tempEndDate.value,
	};

	saveViewToStorage(selectedView.value);

	const newRange: DateRange = {
		startDate: tempStartDate.value,
		endDate: tempEndDate.value,
	};

	emitRangeUpdate(newRange, selectedView.value);

	await nextTick();
	isOpen.value = false;
};

const handleApplyDisposition = async () => {
	const today = new Date();
	today.setHours(0, 0, 0, 0);

	const past = pastDaysCustom.value ? parseInt(pastDaysCustom.value) : pastDays.value || DEFAULT_PAST_DAYS;
	const future = futureDaysCustom.value ? parseInt(futureDaysCustom.value) : futureDays.value || DEFAULT_FUTURE_DAYS;

	const startDate = new Date(today);
	startDate.setDate(today.getDate() - past);

	const endDate = new Date(today);
	endDate.setDate(today.getDate() + future);

	const newRange: DateRange = { startDate, endDate };

	appliedView.value = 'daily_disposition';
	selectedView.value = 'daily_disposition';
	appliedRange.value = newRange;

	saveViewToStorage('daily_disposition');
	saveDispositionConfigToStorage();

	emitRangeUpdate(newRange, 'daily_disposition');

	await nextTick();
	isOpen.value = false;
};

const handleClear = () => {
	rangeError.value = null;
	tempStartDate.value = null;
	tempEndDate.value = null;
	isCleared.value = true;
	appliedView.value = 'custom';
	selectedView.value = 'custom';
	appliedRange.value = { startDate: null, endDate: null };
	lastEmitted.value = {
		startKey: null,
		endKey: null,
		view: null,
	};

	saveViewToStorage('custom');

	const clearedRange: DateRange = { startDate: null, endDate: null };
	emit('update:modelValue', clearedRange);
	isOpen.value = false;
};

const handleCancel = () => {
	isOpen.value = false;
};

const togglePicker = () => {
	isOpen.value = !isOpen.value;

	if (isOpen.value) {
		isCleared.value = false;
		initializeDates();
	}
};

const handleClickOutside = (event: MouseEvent) => {
	if (pickerRef.value && !pickerRef.value.contains(event.target as Node)) {
		isOpen.value = false;
	}
};
</script>

<style scoped>
.picker-modal-backdrop {
	position: fixed;
	top: 0;
	left: 0;
	width: 100vw;
	height: 100vh;
	background-color: transparent;
	z-index: 999;
	pointer-events: auto;
}

.date-range-picker {
	position: relative;
	width: 241px;
	height: 34px;
	display: inline-flex;
	align-items: center;
	border: 1px solid rgba(0, 0, 0, 0.42);
	cursor: pointer;
	gap: 10px;
	opacity: 1;
	border-radius: 4px;
	padding: 8px 12px;
	box-sizing: border-box;
}
.date-range-picker:hover {
	border-color: #9ca3af;
}
.date-range-input {
	display: flex;
	align-items: center;
	width: 100%;
	gap: 8px;
	background: none;
	border: none;
	padding: 0;
	cursor: pointer;
	text-align: left;
}
.date-text {
	flex: 1;
	min-width: 0;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	font-family: Roboto, sans-serif;
	font-weight: 400;
	font-size: 16px;
	line-height: 24px;
	letter-spacing: 0.5px;
	color: rgba(0, 0, 0, 0.87);
}

.calendar-icon {
	flex-shrink: 0;
	width: 24px;
	height: 24px;
	font-size: 24px;
	color: rgba(0, 0, 0, 0.54);
}
.clear-icon {
	flex-shrink: 0;
	width: 24px;
	height: 24px;
	font-size: 18px;
	color: rgba(0, 0, 0, 0.54);
	border-radius: 50%;
	transition: color 0.15s;
}
.clear-icon:hover {
	color: rgba(0, 0, 0, 0.87);
}

.dropdown-panel {
	position: absolute;
	top: 105%;
	left: 0;
	right: auto;
	background: white;
	border: 1px solid #e5e7eb;
	box-shadow:
		0px 5px 14px 0px rgba(0, 0, 0, 0.12),
		0px 9px 10px 0px rgba(0, 0, 0, 0.14);
	z-index: 1000;
	overflow-x: auto;
	overflow-y: auto;

	width: max-content;
	max-width: min(815px, calc(100vw - 32px));

	border-radius: 4px;
	padding: clamp(4px, 0.8vw, 8px);
	box-sizing: border-box;
}

.dropdown-panel.is-disposition {
	max-width: min(616px, calc(100vw - 32px));
}

.panel-layout {
	display: flex;
	flex-direction: column;
	width: 100%;
	gap: clamp(4px, 0.8vw, 8px);
	box-sizing: border-box;
}

.layout-main {
	display: flex;
	flex-direction: row;
	justify-content: flex-start;
	align-items: stretch;
	width: 100%;
	border: 1px solid #eeeeee;
	border-radius: 4px;
	box-sizing: border-box;
	overflow: hidden;
}

/* Sidebar Scaling */
.sidebar {
	width: clamp(140px, 22vw, 206px);
	flex: 0 0 clamp(140px, 22vw, 206px);
}
.sidebar-header {
	display: flex;
	flex-direction: row;
	align-items: center;
	padding: clamp(4px, 0.6vw, 8px) clamp(8px, 1.2vw, 16px);
	gap: 10px;
	width: 100%;
	height: clamp(28px, 3vw, 36px);
	background: #ffffff;
	box-sizing: border-box;
}
.sidebar-title {
	font-family: 'Roboto', sans-serif;
	font-weight: 400;
	font-size: clamp(10px, 0.9vw, 12px);
	line-height: 20px;
	letter-spacing: 0.4px;
	color: rgba(0, 0, 0, 0.6);
}
.sidebar-options {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	padding: 0px;
	width: 100%;
	box-sizing: border-box;
}
.radio-option {
	display: flex;
	flex-direction: row;
	align-items: center;
	padding: clamp(4px, 0.6vw, 8px) clamp(6px, 1vw, 12px);
	width: 100%;
	height: clamp(36px, 3.8vw, 48px);
	cursor: pointer;
	box-sizing: border-box;
}
.selection-control {
	display: flex;
	justify-content: center;
	align-items: center;
	padding: 0 clamp(2px, 0.5vw, 8px);
	width: clamp(20px, 2vw, 32px);
	height: clamp(20px, 2vw, 32px);
}
.radio-icon {
	font-size: clamp(16px, 1.6vw, 24px);
	color: rgba(0, 0, 0, 0.6);
}
.radio-option.active .radio-icon {
	color: #10565c;
}
.option-label {
	padding-left: clamp(4px, 0.6vw, 8px);
	font-family: 'Roboto', sans-serif;
	font-size: clamp(12px, 1.1vw, 16px);
	color: rgba(0, 0, 0, 0.87);
	white-space: nowrap;
}

.divider-line {
	width: 1px;
	min-width: 1px;
	background-color: #eeeeee;
	flex: 0 0 1px;
}

/* Calendar Body Scaling */
.v-picker__body {
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: clamp(6px, 1vw, 12px);
	gap: clamp(4px, 0.8vw, 10px);
	width: clamp(200px, 35vw, 276px);
	flex: 1 1 clamp(200px, 35vw, 276px);
	background: #ffffff;
	box-sizing: border-box;
	font-size: clamp(11px, 1vw, 14px);
}

.calendar-wrapper {
	width: 100%;
	display: flex;
	justify-content: center;
}

/* Disposition Options */
.disposition-view {
	padding: 0;
	width: 100%;
}
.disposition-header {
	display: flex;
	align-items: center;
	padding: clamp(4px, 0.6vw, 8px) 0px;
	background: #ffffff;
}
.disposition-title {
	font-family: 'Roboto', sans-serif;
	font-size: clamp(10px, 0.9vw, 12px);
	line-height: 20px;
	letter-spacing: 0.4px;
	color: rgba(0, 0, 0, 0.6);
	white-space: nowrap;
}
.disposition-column {
	display: flex;
	flex-direction: column;
}
.radio-option-inline {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	margin-bottom: 0.5rem;
	cursor: pointer;
	font-size: clamp(11px, 1vw, 14px);
}
.radio-option-inline input[type='radio'] {
	width: clamp(0.75rem, 1vw, 1rem);
	height: clamp(0.75rem, 1vw, 1rem);
	cursor: pointer;
	accent-color: #0f766e;
}
.custom-input {
	width: clamp(65px, 10vw, 95px);
	padding: clamp(0.15rem, 0.4vw, 0.25rem) clamp(0.4rem, 0.8vw, 0.75rem);
	border: 1px solid #d1d5db;
	border-radius: 0.25rem;
	font-size: clamp(11px, 1vw, 14px);
}
.custom-input:focus {
	outline: none;
	border-color: #0f766e;
	box-shadow: 0 0 0 2px rgba(15, 118, 110, 0.1);
}
.custom-input::placeholder {
	color: #9ca3af;
}

.actions {
	display: flex;
	flex-direction: row;
	justify-content: flex-end;
	align-items: center;
	width: 100%;
	height: clamp(28px, 3vw, 36px);
	background: #ffffff;
	box-sizing: border-box;
}
.btn-apply {
	display: flex;
	align-items: center;
	justify-content: center;
	min-width: clamp(60px, 8vw, 79px);
	height: clamp(28px, 3vw, 36px);
	padding: 0 clamp(8px, 1.2vw, 16px);
	background: #10565c;
	border: none;
	border-radius: 4px;
	cursor: pointer;
	box-sizing: border-box;
}
.btn-content {
	display: flex;
	align-items: center;
	height: 100%;
}
.btn-text {
	font-family: 'Roboto', sans-serif;
	font-weight: 500;
	font-size: clamp(11px, 1vw, 14px);
	letter-spacing: 1.25px;
	text-transform: uppercase;
	color: #ffffff;
	white-space: nowrap;
}
.btn-apply:hover:not(:disabled) {
	background: #0e494e;
}
.btn-apply:disabled {
	background: #d1d5db;
	cursor: not-allowed;
}

.range-error-message {
	display: flex;
	align-items: center;
	gap: 6px;
	padding: clamp(4px, 0.6vw, 6px) clamp(8px, 1vw, 12px);
	background-color: #fef2f2;
	border: 1px solid #fee2e2;
	border-radius: 4px;
	color: #dc2626;
	font-family: 'Roboto', sans-serif;
	font-size: clamp(11px, 0.9vw, 13px);
	font-weight: 500;
	width: 100%;
	box-sizing: border-box;
}
.range-error-message .error-icon {
	font-size: clamp(14px, 1.2vw, 18px);
	color: #dc2626;
}

.dropdown-enter-active,
.dropdown-leave-active {
	transition:
		opacity 0.2s,
		transform 0.2s;
}
.dropdown-enter-from,
.dropdown-leave-to {
	opacity: 0;
	transform: translateY(-8px);
}
.hidden-radio {
	position: absolute;
	opacity: 0;
	width: 0;
	height: 0;
	pointer-events: none;
	margin: 0;
	padding: 0;
}

@media (max-width: 1600px) {
	.dropdown-panel,
	.dropdown-panel.is-disposition {
		width: fit-content;
		max-width: min(340px, calc(100vw - 24px));
		max-height: 70vh;
	}

	.layout-main {
		flex-direction: column;
		max-height: 80vh;
		overflow-y: hidden;
	}

	.sidebar {
		width: 100%;
		flex: 0 0 auto;
	}

	.sidebar-header {
		display: none;
	}

	.sidebar-options {
		flex-direction: row;
		flex-wrap: wrap;
		gap: 4px;
		padding: 6px;
		background: #f9fafb;
	}

	.radio-option {
		width: auto;
		height: 32px;
		padding: 4px 8px;
		border: 1px solid #e5e7eb;
		border-radius: 4px;
		background: #ffffff;
	}

	.divider-line {
		width: 100%;
		min-width: 0;
		height: 1px;
		min-height: 1px;
		flex: 0 0 1px;
	}

	.v-picker__body {
		width: 100%;
		flex: 0 0 auto;
		padding: 8px;
		box-sizing: border-box;
	}
}

.btn-text-only {
	background: transparent;
	border: none;
	padding: 0 clamp(8px, 1.2vw, 16px);
	height: clamp(28px, 3vw, 36px);
	font-family: 'Roboto', sans-serif;
	font-weight: 500;
	font-size: clamp(11px, 1vw, 14px);
	letter-spacing: 1.25px;
	text-transform: uppercase;
	color: #10565c;
	cursor: pointer;
	border-radius: 4px;
	transition: background-color 0.2s ease;
}

.btn-text-only:hover {
	background-color: rgba(16, 86, 92, 0.08);
}
</style>
